import { useEffect, useState, useRef } from "react";
import {
  AiOutlineMenu,
  AiOutlineShoppingCart,
  AiOutlineClose,
  AiFillStar,
} from "react-icons/ai";
import { BsChevronDown } from "react-icons/bs";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Link, matchPath, useLocation } from "react-router-dom";

import logo from "../../assets/Logo/Logo-Full-Light.png";
import { NavbarLinks } from "../../data/navbar-links";
import { apiConnector } from "../../services/apiConnector";
import { categories } from "../../services/apis";
import { ACCOUNT_TYPE } from "../../ultilites/constants";
import ProfileDropdown from "../core/Auth/ProfileDropDown";

function Navbar() {
  const { token } = useSelector((state) => state.auth);
  const { user } = useSelector((state) => state.profile);
  const { totalItems } = useSelector((state) => state.cart);
  const location = useLocation();

  const [subLinks, setSubLinks] = useState([]);
  const [abroadLinks, setAbroadLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [isAbroadOpen, setIsAbroadOpen] = useState(false);
  const [activeAbroadCategory, setActiveAbroadCategory] = useState(null);
  const [activeProgram, setActiveProgram] = useState(null);
  const abroadTimeoutRef = useRef(null);

  const catalogRef = useRef(null);
  const abroadRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleAbroadEnter = () => {
    if (abroadTimeoutRef.current) {
      clearTimeout(abroadTimeoutRef.current);
      abroadTimeoutRef.current = null;
    }
    setIsAbroadOpen(true);
  };

  const handleAbroadLeave = () => {
    abroadTimeoutRef.current = setTimeout(() => {
      setIsAbroadOpen(false);
    }, 300);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [localRes, abroadRes] = await Promise.all([
          apiConnector("GET", categories.CATEGORIES_API),
          apiConnector("GET", categories.CATEGORIES_Abroad_API),
        ]);
        setSubLinks(localRes.data.data);
        setAbroadLinks(abroadRes.data.data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchCategories();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        catalogRef.current &&
        !catalogRef.current.contains(event.target) &&
        abroadRef.current &&
        !abroadRef.current.contains(event.target) &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsCatalogOpen(false);
        setIsAbroadOpen(false);
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const matchRoute = (route) => {
    return matchPath({ path: route }, location.pathname);
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div
      className={`flex h-16 items-center justify-center border-b-[1px] border-b-richblack-50 bg-white dark:bg-[#121212] ${
        location.pathname !== "/" ? "bg-white" : ""
      } transition-all duration-200`}
    >
      <div className="flex lg:w-11/12 w-full max-w-maxContent items-center justify-between">
        <Link to="/">
          <motion.img
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            src={logo}
            alt="Logo"
            width={160}
            loading="lazy"
            className="h-[80px]"
          />
        </Link>

        <nav className="hidden lg:block dark:text-white">
          <ul className="flex gap-x-6 text-richblack-500 dark:text-white">
            {NavbarLinks.map((link, index) => (
              <li key={index}>
                {link.title === "Catalog" ? (
                  <div
                    onMouseEnter={() => setIsCatalogOpen(true)}
                    onMouseLeave={() => setIsCatalogOpen(false)}
                    className={`group relative flex cursor-pointer items-center gap-1 ${
                      matchRoute("/catalog/:catalogName")
                        ? "text-[#CD1412]"
                        : "text-richblack-600 dark:text-white"
                    }`}
                  >
                    <p className="flex items-center gap-1">{link.title}</p>

                    <BsChevronDown />
                    {isCatalogOpen && (
                      <div
                        ref={catalogRef}
                        className="absolute left-[50%] top-[-50%] z-[1000] flex w-[200px] translate-x-[-50%] translate-y-[3em] flex-col rounded-lg bg-white shadow-[0px_1px_20px_-3px_#a0aec0] p-4 text-richblack-900 transition-all duration-150 lg:w-[300px]"
                      >
                        <div className="absolute left-[50%] top-0 -z-10 h-7 w-10 translate-x-[0%] translate-y-[-30%] rotate-45 select-none rounded bg-white"></div>
                        {loading ? (
                          <p className="text-center">Loading...</p>
                        ) : subLinks.length ? (
                          subLinks
                            .filter((subLink) => subLink?.courses?.length > 0)
                            .map((subLink, i) => (
                              <Link
                                to={`/catalog/${subLink.name
                                  .split(" ")
                                  .join("-")
                                  .toLowerCase()}`}
                                className="rounded-lg bg-transparent py-4 pl-4 hover:bg-[#CD1412] text-richblack-600 hover:text-white"
                                key={i}
                              >
                                {subLink.name}
                              </Link>
                            ))
                        ) : (
                          <p className="text-center">No Courses Found</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : link.title === "Abroad Education" ? (
                  <div
                    onMouseEnter={handleAbroadEnter}
                    onMouseLeave={handleAbroadLeave}
                    className={`relative ${
                      matchRoute("/abroad-education/:courseName/:countryName")
                        ? "text-[#CD1412]"
                        : "text-richblack-600 dark:text-white"
                    }`}
                  >
                    <div className="cursor-pointer flex items-center gap-1">
                      {link.title}
                      <BsChevronDown />
                    </div>

                    {isAbroadOpen && (
                      <div
                        className="absolute left-[50%] top-[-50%] z-[1000] flex w-[200px] translate-x-[-50%] translate-y-[3em] flex-col rounded-lg bg-white shadow-[0px_1px_20px_-3px_#a0aec0] p-4 text-richblack-900 transition-all duration-150 lg:w-[300px]"
                        onMouseEnter={handleAbroadEnter}
                        onMouseLeave={handleAbroadLeave}
                      >
                        <div className="absolute left-[50%] top-0 -z-10 h-7 w-10 translate-x-[0%] translate-y-[-30%] rotate-45 select-none rounded bg-white"></div>
                        {abroadLinks.map((link, idx) => (
                          <div
                            key={idx}
                            className="relative group"
                            onMouseEnter={() => {
                              setTimeout(() => {
                                setActiveAbroadCategory(link.name);
                              }, 50);
                            }}
                          >
                            <div className="py-2 px-4 hover:bg-[#CD1412] hover:text-white cursor-pointer rounded">
                              {link.name}
                            </div>

                            {/* Nested submenu */}
                            {activeAbroadCategory === link.name &&
                              link.programs && (
                                <div className="absolute top-0 left-full ml-[6.5%] w-[250px] bg-white shadow-md rounded-lg p-2 z-50">
                                  {link.programs.map((program, i) => (
                                    <div
                                      key={i}
                                      className="mb-2"
                                      onMouseEnter={() => {
                                        setTimeout(() => {
                                          setActiveProgram(program.name);
                                        }, 50);
                                      }}
                                    >
                                      <div className="py-2 px-4 hover:bg-[#CD1412] hover:text-white cursor-pointer rounded">
                                        {program.name}
                                      </div>

                                      {activeProgram === program.name &&
                                        program.module && (
                                          <div className="absolute top-0 left-full ml-[1.5%] w-[250px] max-h-64 overflow-y-auto bg-white shadow-md rounded-lg p-2 z-50">
                                            {program.module.map((mod, i) => (
                                              <div key={i} className="mb-2">
                                                <div className="py-2 px-4 hover:bg-[#CD1412] hover:text-white cursor-pointer rounded">
                                                  <Link
                                                    to={`/abroad-education/${activeProgram}/${mod.name
                                                      .split(" ")
                                                      .join("-")
                                                      .toLowerCase()}/${
                                                      mod._id
                                                    }`}
                                                    key={i}
                                                    onClick={() => {
                                                      setIsAbroadOpen(false);
                                                    }}
                                                  >
                                                    {mod.name}
                                                  </Link>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link to={link?.path}>
                    <p
                      className={`${
                        matchRoute(link?.path)
                          ? "text-[#CD1412]"
                          : "text-richblack-600 dark:text-richblack-5"
                      }`}
                    >
                      <div className="relative flex items-center gap-2">
                        <span>{link.title}</span>
                        {link.title === "FMGE through AI" && (
                          <motion.span
                            className="relative px-2 py-0.5 rounded-md font-bold text-white"
                            style={{
                              backgroundColor: "#b91c1c",
                              boxShadow:
                                "0 0 8px , 0 0 15px #ff1a1a, 0 0 20px #ff1a1a",
                            }}
                            animate={{
                              scale: [1, 1.25, 1],
                              rotate: [0, 4, -4, 0],
                            }}
                            transition={{
                              duration: 1.2,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          >
                            NEW
                          </motion.span>
                        )}
                      </div>
                    </p>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <button
          onClick={toggleMenu}
          className="mr-4 lg:hidden bg-[#C91513] text-richblack-5 w-10 h-10 flex items-center justify-center rounded-full"
        >
          {isMenuOpen ? (
            <AiOutlineClose fontSize={58} fill="#AFB2BF" />
          ) : (
            <AiOutlineMenu fontSize={24} fill="#AFB2BF" />
          )}
        </button>

        {isMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="absolute top-14 right-0 z-50 w-[60%] md:w-[50%] md:min-w-[300px] bg-white shadow-xl p-4 rounded-lg"
          >
            <ul className="space-y-4 text-richblack-700 dark:text-white">
              {NavbarLinks.map((link, index) => (
                <li key={index}>
                  {link.title === "Catalog" ? (
                    <>
                      <button
                        onClick={() => setIsCatalogOpen((prev) => !prev)}
                        className="flex items-center justify-between w-full"
                      >
                        <span>Catalog</span>
                        <BsChevronDown
                          className={`transform transition-transform duration-200 ${
                            isCatalogOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isCatalogOpen && (
                        <ul className="mt-2 pl-4 space-y-2">
                          {subLinks.map(
                            (subLink, i) =>
                              subLink?.courses?.length > 0 && (
                                <li key={i}>
                                  <Link
                                    to={`/catalog/${subLink.name
                                      .split(" ")
                                      .join("-")
                                      .toLowerCase()}`}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="hover:text-[#CD1412]"
                                  >
                                    {subLink.name}
                                  </Link>
                                </li>
                              )
                          )}
                        </ul>
                      )}
                    </>
                  ) : link.title === "Abroad Education" ? (
                    <>
                      <button
                        onClick={() => setIsAbroadOpen((prev) => !prev)}
                        className="flex items-center justify-between w-full"
                      >
                        <span>Abroad Education</span>
                        <BsChevronDown
                          className={`transform transition-transform duration-200 ${
                            isAbroadOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isAbroadOpen && (
                        <ul className="mt-2 pl-4 space-y-3">
                          {abroadLinks.map((cat, i) => (
                            <li key={i}>
                              <p className="font-semibold">{cat.name}</p>
                              {cat.programs &&
                                cat.programs.map((program, j) => (
                                  <div key={j} className="ml-2">
                                    <p className="text-sm font-medium mt-2">
                                      {program.name}
                                    </p>
                                    {program.module &&
                                      program.module.map((mod, k) => (
                                        <Link
                                          key={k}
                                          to={`/abroad-education/${
                                            program.name
                                          }/${mod.name
                                            .split(" ")
                                            .join("-")
                                            .toLowerCase()}/${mod._id}`}
                                          onClick={() => setIsMenuOpen(false)}
                                          className="block ml-4 text-sm hover:text-[#CD1412]"
                                        >
                                          {mod.name}
                                        </Link>
                                      ))}
                                  </div>
                                ))}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMenuOpen(false)}
                      className="block hover:text-[#CD1412]"
                    >
                      {link.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="hidden lg:flex items-center gap-x-4 ">
          {user &&
            user?.accountType !== ACCOUNT_TYPE.INSTRUCTOR &&
            user?.accountType !== ACCOUNT_TYPE.ADMIN && (
              <Link to="/dashboard/cart" className="relative">
                <AiOutlineShoppingCart className="text-2xl text-richblack-200 dark:text-white" />
                {totalItems > 0 && (
                  <span className="absolute -bottom-2 -right-2 grid h-5 w-5 place-items-center overflow-hidden rounded-full bg-[#CD1412] text-center text-xs font-bold text-white">
                    {totalItems}
                  </span>
                )}
              </Link>
            )}
          {token === null && (
            <>
              <Link to="/login">
                <button className="rounded-[8px] border border-richblack-700 bg-richblack-800 px-[12px] py-[8px] text-richblack-100">
                  Log in
                </button>
              </Link>
              <Link to="/signup">
                <button className="rounded-[8px] border border-richblack-700 bg-richblack-800 px-[12px] py-[8px] text-richblack-100">
                  Sign up
                </button>
              </Link>
            </>
          )}
          {token !== null && <ProfileDropdown />}
        </div>
      </div>
    </div>
  );
}

export default Navbar;
